import React, { useState } from 'react';
import { Box, Typography, Stack, Collapse, Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

function CardDeletionRequest({ isMarkedForDeletion, isReadOnly, updateDraft, variant = 'clinical' }) {
    const [open, setOpen] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

    const confirmationText = {
        approved: {
            title: 'Confirm Approve Deactivation',
            body: 'Are you sure you want to approve this card deactivation request? If there are any open reviews on this card, they will need to be closed or marked as not applicable and you will no longer be able to review this card',
        },
        approved_and_implemented: {
            title: 'Confirm Approve & Implement Deactivation',
            body: 'Are you sure you want to approve this card deactivation request and implement the deactivation immediately? This will close any existing reviews on this card and you will no longer be able to review them.', 
        },
        denied: {
            title: 'Confirm Denial',
            body: 'Are you sure you want to deny this card deactivation request?',
        },
    };

    const handleActionSelected = (action) => {
        setConfirmDialog({ open: true, action });
    };

    // logging is done in parent via the updateDraft function
    const handleConfirm = () => {
        const action = confirmDialog.action;
        setConfirmDialog({ open: false, action: null });
        if (action === 'approved') {
            updateDraft('applied');
        } else if (action === 'approved_and_implemented') {
            updateDraft('implemented');
        } else if (action === 'denied') {
            updateDraft('denied');
            setOpen(false);
        }
    };

    const handleCancel = () => {
        setConfirmDialog({ open: false, action: null });
    };

    if (!isMarkedForDeletion) return null;

    return (
        <Box sx={{ px: 2, py: 1, backgroundColor: theme => theme.palette.background.default, borderRadius: 2 }}>
            <Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant='heavy'>Card Deactivation Request</Typography>
                </Stack>
                <Collapse in={open}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: theme => theme.palette.grey[100], borderRadius: 1, p: 1, mt: 1 }}>
                        <Box>
                            <Typography variant="caption" color="error" fontWeight={600}>
                                This card was marked for deactivation by the physician.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">   
                                The physician believes this card is no longer needed and recommends it be removed from the system, or marked as inactive. Please review the physician's notes for more context and either approve or deny this request.
                            </Typography>
                        </Box>
                        { !isReadOnly &&
                            <DeactivationActionDropdown
                                onActionSelected={handleActionSelected}
                                variant={variant}
                            />
                        }
                    </Stack>
                </Collapse>
            </Stack>
            <Dialog open={confirmDialog.open} onClose={handleCancel}>
                <DialogTitle>{confirmDialog.action && confirmationText[confirmDialog.action]?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.action && confirmationText[confirmDialog.action]?.body}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button onClick={handleConfirm} variant="contained" color="primary">Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function DeactivationActionDropdown({ onActionSelected, variant }) {

    return (
        <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
                displayEmpty
                value=""
                onChange={e => onActionSelected(e.target.value)}
                sx={{ fontSize: '0.8125rem' }}
            >
                <MenuItem value="">Select Action</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
                {variant === 'clinical' && <MenuItem value="approved">Approved</MenuItem>}
                <MenuItem value="approved_and_implemented">Approved & Implemented</MenuItem>
            </Select>
        </FormControl>
    );
}

export default CardDeletionRequest;